import { useBreakpointValue, useDisclosure } from "@chakra-ui/react"
import { flattenEdges, withTrailingSlash } from "@pathwright/utils"
import { getUrlFromLinkTo } from "@pathwright/ux/src/components/link/Link"
import { usePathwrightContext } from "@pathwright/features/src/modules/pathwright/PathwrightContext"
import {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react"
import { useLocation } from "react-router-dom"
import { createContext, useContextSelector } from "use-context-selector"
import { StringParam, useQueryParam } from "use-query-params"
import {
  CoverPageCategoryFragment,
  CoverPageCategoryFragmentDoc,
  CoverPageContributorFragment,
  CoverPageContributorFragmentDoc,
  CoverPageRelatedResourcesQueryVariables,
  CoverPageResourceFragment,
  PositionedAuthorFragmentDoc,
  PositionedCategoryFragmentDoc,
  ResourceType,
  useCoverPageLicensorRelatedResourcesQuery,
  useCoverPageLicensorResourceQuery,
  useCoverPageOffersQuery,
  useCoverPageRelatedResourcesQuery,
  useCoverPageResourceQuery,
  useUpdateCoverPageResourceMutation
} from "@pathwright/graphql-client/src/generated"
import { getApolloClient } from "../pathwright/PathwrightClient"
import { sortPositionedObjects } from "../positioning/PositioningContext"
import { RESOURCE_TYPE_LABELS } from "../resource/constants"
import {
  COVER_PAGE_CLOSE_TARGET_STATE_KEY,
  clearActiveCoverPageCloseTarget,
  normalizeCloseTargetCandidate,
  resolveCoverPageCloseTarget,
  setActiveCoverPageCloseTarget
} from "./coverPageCloseTarget"
import { useCoverPageHashScroll } from "./coverPageHashNavigation"
import { sortCoverPageOffers } from "./coverPageOfferOrdering"
import { getCoverPageResourcePath } from "./coverPageResourcePath"
import { getCoverPageAmbientResourceFields } from "./design/utils"
import { useCoverPagePreviewData } from "./path-preview/utils"
import {
  getCategoryPositioningContext,
  getContributorPositioningContext
} from "./positioning"
import { readCoverPageResourceSeed } from "./readCoverPageResourceSeed"
import { CoverPageContextType, CoverPageProps } from "./types"

const CoverPageContext = createContext<CoverPageContextType>(
  {} as CoverPageContextType
)

const useMoreResources = (
  resource: CoverPageResourceFragment | null,
  resourceId: number,
  licensorSchoolId?: number | null
) => {
  const resourceCategories = resource?.categories
  const categories = useMemo(
    () => flattenEdges(resourceCategories),
    [resourceCategories]
  )

  const categoryIds =
    categories?.map((category: CoverPageCategoryFragment) => category.id) || []

  const variables: CoverPageRelatedResourcesQueryVariables = {
    idFilter: { neq: resourceId.toString() },
    categoryIdFilter: {
      in: categoryIds.map((id) => id.toString())
    },
    resourceTypeFilter: { eq: ResourceType.Course },
    orderBy: "list_order,-created",
    first: 10
  }

  const relatedResourcesQuery = useCoverPageRelatedResourcesQuery({
    variables,
    skip: !categoryIds.length || !!licensorSchoolId
  })

  const licensorRelatedResourcesQuery =
    useCoverPageLicensorRelatedResourcesQuery({
      variables: {
        ...variables,
        licensorSchoolId: licensorSchoolId!
      },
      skip: !categoryIds.length || !licensorSchoolId
    })

  const relatedResourcesData =
    relatedResourcesQuery.data || relatedResourcesQuery.previousData
  const licensorRelatedResourcesData =
    licensorRelatedResourcesQuery.data ||
    licensorRelatedResourcesQuery.previousData

  const resources = flattenEdges(
    relatedResourcesData?.resources ||
      licensorRelatedResourcesData?.context?.school?.resources ||
      []
  ) as CoverPageResourceFragment[]

  return resources
}

export function useCoverPageContext<T = CoverPageContextType>(
  selector?: (value: CoverPageContextType) => T
): T {
  if (selector) {
    return useContextSelector(CoverPageContext, selector)
  }
  return useContextSelector(CoverPageContext, (v) => v) as T
}

type CoverPageProviderProps = CoverPageProps & {
  children: ReactNode
}

const CoverPageProvider = ({ children, ...props }: CoverPageProviderProps) => {
  const [videoFocused, setVideoFocused] = useState(false)
  const [backgroundVideoMuted, setBackgroundVideoMuted] = useState(true)
  const [showVideoMuteButton, setShowVideoMuteButton] = useState<boolean>(false)
  const [loadingNextCoverPage, setLoadingNextCoverPage] = useState(false)
  const [offerId, setOfferId] = useState<number | null>(null)
  const [optimisticOfferPositions, setOptimisticOfferPositions] =
    useState<Record<number, string> | null>(null)
  const [offerConversionIntentId, setOfferConversionIntentId] = useState<
    number | null
  >(null)
  const [isLoadingImmersiveRoute, setIsLoadingImmersiveRoute] = useState(false)
  const [closeTarget, setCloseTarget] = useState<string>(() =>
    resolveCoverPageCloseTarget(
      getUrlFromLinkTo(props.closeTarget),
      props.coverRoute
    )
  )

  const client = getApolloClient()
  const { me } = usePathwrightContext()

  const location = useLocation<{
    [COVER_PAGE_CLOSE_TARGET_STATE_KEY]?: string
  }>()
  const [queryParamInviteToken] = useQueryParam("ic", StringParam)
  const resourceInviteTokenMap = useRef<Record<number, string>>({})

  // Map resourceId to inviteToken. We store these so we don't lose them due to navigation.
  const inviteToken = useMemo(() => {
    if (queryParamInviteToken) {
      resourceInviteTokenMap.current[props.resourceId] = queryParamInviteToken
    }
    return resourceInviteTokenMap.current[props.resourceId] ?? null
  }, [props.resourceId, queryParamInviteToken])

  // Used mainly for scrolling to sections
  const offersSectionRef = useRef<HTMLDivElement>(null)
  const previewSectionRef = useRef<HTMLDivElement>(null)
  const contributorSectionRef = useRef<HTMLDivElement>(null)
  const descriptionSectionRef = useRef<HTMLDivElement>(null)
  const moreSectionRef = useRef<HTMLDivElement>(null)
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  // Header collapsed/trigger shared state
  const headerTriggerRef = useRef<HTMLDivElement>(null)
  const [headerCollapsed, setHeaderCollapsed] = useState(false)

  const designPanelDisclosure = useDisclosure()
  const isDesignMode = useMemo(
    () => !!designPanelDisclosure.open,
    [designPanelDisclosure?.open]
  )

  // Resolved on the first render: rendering the mobile toolbar first gets it
  // measured, which collapses the cover page to zero width until it's replaced.
  const isMobile = useBreakpointValue(
    {
      base: true,
      md: false
    },
    { ssr: false }
  )

  const coverPageResourceQuery = useCoverPageResourceQuery({
    variables: {
      id: props.resourceId,
      contributorPositioningContext: getContributorPositioningContext(
        props.resourceId
      ),
      categoryPositioningContext: getCategoryPositioningContext(
        props.resourceId
      )
    },
    skip: !props.resourceId || !!props.licensorSchoolId
  })
  const {
    data: resourceData,
    loading: resourceLoading,
    refetch: refetchResourceQuery
  } = coverPageResourceQuery

  const {
    data: licensorResourceData,
    loading: licensorResourceLoading,
    refetch: refetchLicensorResourceQuery
  } = useCoverPageLicensorResourceQuery({
    variables: {
      id: props.resourceId,
      licensorSchoolId: props.licensorSchoolId!,
      contributorPositioningContext: getContributorPositioningContext(
        props.resourceId
      ),
      categoryPositioningContext: getCategoryPositioningContext(
        props.resourceId
      )
    },
    skip: !props.resourceId || !props.licensorSchoolId
  })

  const resource =
    resourceData?.resource ||
    licensorResourceData?.context?.school?.resource ||
    null
  const partialResource = readCoverPageResourceSeed(client, props.resourceId)
  const paintResource = resource ?? partialResource
  const resourceId = props.resourceId

  // Memoize flattened data to prevent unnecessary rerenders
  const contributors = useMemo(
    () => sortPositionedObjects(flattenEdges(resource?.contributors) || []),
    [resource?.contributors]
  )
  const categories = useMemo(
    () => sortPositionedObjects(flattenEdges(resource?.categories) || []),
    [resource?.categories]
  )

  const {
    data: offerData,
    loading: offersLoading,
    refetch: refetchOffersQuery
  } = useCoverPageOffersQuery({
    variables: {
      context: {
        resource_id: resourceId,
        licensor_school_id: props.licensorSchoolId,
        invite_token: inviteToken
      },
      first: 100
    },
    skip: !resourceId
  })

  const [updateCoverPageResource] = useUpdateCoverPageResourceMutation()

  const refetchCoverPageResource = useCallback(() => {
    if (!props.resourceId) return Promise.resolve(null)
    if (props.licensorSchoolId) return refetchLicensorResourceQuery()
    return refetchResourceQuery()
  }, [
    props.resourceId,
    props.licensorSchoolId,
    refetchLicensorResourceQuery,
    refetchResourceQuery
  ])

  const refetchCoverPageOffers = useCallback(() => {
    if (!resourceId) return Promise.resolve(null)
    return refetchOffersQuery()
  }, [resourceId, refetchOffersQuery])

  const updateCoverPageResourceDetails = useCallback<
    CoverPageContextType["updateCoverPageResourceDetails"]
  >(
    async (details) => {
      if (!resource) {
        throw new Error(
          "Unable to update resource details: resource is unavailable."
        )
      }

      const resourceType = RESOURCE_TYPE_LABELS[resource.resource_type]
      if (!resourceType) {
        throw new Error(
          `Unable to update resource details: unsupported resource type ${resource.resource_type}.`
        )
      }

      const contributorPositioningContext = getContributorPositioningContext(
        resource.id
      )
      const categoryPositioningContext = getCategoryPositioningContext(
        resource.id
      )

      const authorIds = details.authors
        ? ([] as number[]).concat(details.authors as number[])
        : contributors.map((contributor) => contributor.id)

      const optimisticContributors = authorIds.reduce(
        (acc, authorId) => {
          const coverPageContributor = client.readFragment({
            id: `Author:${authorId}`,
            fragment: CoverPageContributorFragmentDoc,
            fragmentName: "CoverPageContributor",
            variables: {
              contributorPositioningContext
            }
          })

          const contributor = client.readFragment({
            id: `Author:${authorId}`,
            fragment: PositionedAuthorFragmentDoc,
            fragmentName: "PositionedAuthor",
            variables: {
              positioningContext: contributorPositioningContext
            }
          })

          const contributorData = coverPageContributor
            ? coverPageContributor
            : contributor
            ? {
                ...contributor,
                position: ""
              }
            : null

          const existingContributor = contributors.find(
            (contributor) => contributor.id === authorId
          )
          const nextContributor = contributorData || existingContributor

          if (nextContributor) {
            acc.total++
            acc.edges.push({
              __typename: "AuthorEdge",
              node: nextContributor
            })
          }

          return acc
        },
        {
          __typename: "AuthorConnection" as const,
          total: 0,
          edges: [] as {
            __typename: "AuthorEdge"
            node: CoverPageContributorFragment
          }[]
        }
      )

      const categoryIds = details.categories
        ? ([] as number[]).concat(details.categories as number[])
        : categories.map((category) => category.id)

      const optimisticCategories = categoryIds.reduce(
        (acc, categoryId) => {
          const coverPageCategory = client.readFragment({
            id: `Category:${categoryId}`,
            fragment: CoverPageCategoryFragmentDoc,
            fragmentName: "CoverPageCategory",
            variables: {
              categoryPositioningContext
            }
          })

          const category = client.readFragment({
            id: `Category:${categoryId}`,
            fragment: PositionedCategoryFragmentDoc,
            fragmentName: "PositionedCategory",
            variables: {
              positioningContext: categoryPositioningContext
            }
          })

          const categoryData = coverPageCategory
            ? coverPageCategory
            : category
            ? {
                ...category,
                position: ""
              }
            : null

          const existingCategory = categories.find(
            (category) => category.id === categoryId
          )
          const nextCategory = categoryData || existingCategory

          if (nextCategory) {
            acc.total++
            acc.edges.push({
              __typename: "CategoryEdge",
              node: nextCategory
            })
          }

          return acc
        },
        {
          __typename: "CategoryConnection" as const,
          total: 0,
          edges: [] as {
            __typename: "CategoryEdge"
            node: CoverPageCategoryFragment
          }[]
        }
      )

      return updateCoverPageResource({
        variables: {
          id: resource.id,
          resource_type: resourceType,
          contributorPositioningContext,
          categoryPositioningContext,
          ...details,
          name: details.name ?? resource.name
        },
        optimisticResponse: {
          __typename: "Mutations",
          updateResource: {
            __typename: "Resource",
            ...resource,
            ...details,
            ...getCoverPageAmbientResourceFields(
              !!resource.is_ambient_background
            ),
            name: details.name ?? resource.name,
            cover_display_image: details.cover_image ?? resource.cover_display_image,
            cover_image: details.cover_image ?? resource.cover_image,
            image: details.cover_image ?? resource.image,
            image_raw: details.cover_image ?? resource.image_raw,
            ...(details.background_image !== undefined
              ? {
                  cover_background_image: details.background_image,
                  cover_blurred_background: details.background_image
                }
              : {}),
            contributors: optimisticContributors,
            categories: optimisticCategories
          }
        }
      })
    },
    [
      categories,
      client,
      contributors,
      resource,
      updateCoverPageResource
    ]
  )

  const loading =
    !resource ||
    resourceLoading ||
    licensorResourceLoading ||
    offersLoading
  const offerSources = useMemo(() => {
    const sources =
      (flattenEdges(
        offerData?.offerConnections
      ) as CoverPageContextType["offers"]) || []

    if (!optimisticOfferPositions) return sources

    return sources.map((offer) => {
      const position = optimisticOfferPositions[offer.id]

      if (!position) return offer

      return {
        ...offer,
        position
      }
    })
  }, [offerData?.offerConnections, optimisticOfferPositions])

  const offers = useMemo(
    () => sortCoverPageOffers(offerSources),
    [offerSources]
  )

  const updateCoverPageOfferPositions = useCallback<
    CoverPageContextType["updateCoverPageOfferPositions"]
  >((order) => {
    setOptimisticOfferPositions(
      order
        ? order.reduce<Record<number, string>>((positions, storeOffer) => {
            positions[storeOffer.id] = storeOffer.position
            return positions
          }, {})
        : null
    )
  }, [])

  const routeOfferId =
    props.immersiveRoute?.name === "offer"
      ? Number(props.immersiveRoute.id) || null
      : null

  useEffect(() => {
    setOfferId(routeOfferId)
  }, [routeOfferId])

  const offer = useMemo(
    () => offers?.find((offer) => offer.id === offerId) || null,
    [offers, offerId]
  )

  const moreResources = useMoreResources(
    resource,
    resourceId,
    props.licensorSchoolId
  )

  const { previewNavItems, canPreview } = useCoverPagePreviewData({
    resource
  })

  const sectionMountKey = [
    resourceId,
    offers?.length ?? 0,
    contributors?.length ?? 0,
    categories?.length ?? 0,
    moreResources?.length ?? 0,
    canPreview ? 1 : 0
  ].join("|")

  const { scrollToSection } = useCoverPageHashScroll({
    scrollContainerRef,
    sectionMountKey
  })

  // TODO: is this still needed?
  useEffect(() => {
    if (canPreview) {
      requestAnimationFrame(() => {
        window.dispatchEvent(new Event("resize"))
      })
    }
  }, [canPreview])

  const immersiveActive = useMemo(() => {
    return props.immersiveRoute !== null
  }, [props.immersiveRoute])

  const immersiveBackTo = useMemo(
    () => withTrailingSlash(props.coverRoute || ""),
    [props.coverRoute]
  )

  const getResourceCoverPath = useCallback<
    CoverPageContextType["getResourceCoverPath"]
  >(
    (resource) =>
      getCoverPageResourcePath(
        resource,
        props.coverBaseRoute || "/",
        props.licensorSchoolId ? "id" : "slug"
      ),
    [props.coverBaseRoute, props.licensorSchoolId]
  )

  // Resolve the close target from the state or props
  useEffect(() => {
    const candidate = normalizeCloseTargetCandidate(
      (typeof location.state?.[COVER_PAGE_CLOSE_TARGET_STATE_KEY] === "string"
        ? location.state[COVER_PAGE_CLOSE_TARGET_STATE_KEY]
        : null) || getUrlFromLinkTo(props.closeTarget),
      props.coverRoute
    )

    if (candidate && candidate !== closeTarget) {
      setCloseTarget(candidate)
    }
  }, [location.state, props.closeTarget, props.coverRoute, closeTarget])

  useEffect(() => {
    setActiveCoverPageCloseTarget(closeTarget)
    return () => clearActiveCoverPageCloseTarget()
  }, [closeTarget])

  // Reset loading/edit state when resource changes (new Cover Page loaded)
  useEffect(() => {
    if (resource) {
      setLoadingNextCoverPage(false)
      setOfferConversionIntentId(null)
    }
  }, [resource?.id])

  const contextValue = useMemo(() => {
    return {
      // Props
      resourceId,
      coverRoute: props.coverRoute,
      coverBaseRoute: props.coverBaseRoute,
      licensorSchoolId: props.licensorSchoolId,
      onClose: props.onClose,
      getImmersiveRoutePath: props.getImmersiveRoutePath,
      immersiveRoute: props.immersiveRoute,

      // GQL
      resource,
      partialResource,
      paintResource,
      contributors,
      categories,
      offers,
      offersLoading,
      moreResources,

      // Refs
      offersSectionRef,
      previewSectionRef,
      descriptionSectionRef,
      contributorSectionRef,
      moreSectionRef,
      scrollContainerRef,
      headerTriggerRef,

      // State
      offerId,
      setOfferId,
      offerConversionIntentId,
      setOfferConversionIntentId,
      videoFocused,
      setVideoFocused,
      backgroundVideoMuted,
      setBackgroundVideoMuted,
      headerCollapsed,
      setHeaderCollapsed,
      showVideoMuteButton,
      setShowVideoMuteButton,
      loadingNextCoverPage,
      setLoadingNextCoverPage,
      closeTarget,
      isLoadingImmersiveRoute,
      setIsLoadingImmersiveRoute,

      // Context
      me,

      // Handlers
      refetchCoverPageResource,
      refetchCoverPageOffers,
      updateCoverPageOfferPositions,
      updateCoverPageResourceDetails,
      scrollToSection,
      designPanelDisclosure,

      // Computed
      inviteToken: inviteToken ?? null,
      offer,
      canPreview,
      immersiveBackTo,
      getResourceCoverPath,
      queriesAreLoading: loading,
      previewNavItems,
      immersiveActive,
      isMobile,
      isDesignMode
    } satisfies CoverPageContextType
  }, [
    resourceId,
    props.coverRoute,
    props.coverBaseRoute,
    props.licensorSchoolId,
    props.onClose,
    props.getImmersiveRoutePath,
    props.immersiveRoute,
    resource,
    partialResource,
    paintResource,
    contributors,
    categories,
    offers,
    offersLoading,
    moreResources,
    offerId,
    offerConversionIntentId,
    videoFocused,
    backgroundVideoMuted,
    headerCollapsed,
    showVideoMuteButton,
    loadingNextCoverPage,
    closeTarget,
    isLoadingImmersiveRoute,
    me,
    refetchCoverPageResource,
    refetchCoverPageOffers,
    updateCoverPageOfferPositions,
    updateCoverPageResourceDetails,
    scrollToSection,
    designPanelDisclosure,
    inviteToken,
    offer,
    canPreview,
    immersiveBackTo,
    getResourceCoverPath,
    loading,
    previewNavItems,
    immersiveActive,
    isMobile,
    isDesignMode
  ])

  return (
    <CoverPageContext.Provider value={contextValue}>
      {children}
    </CoverPageContext.Provider>
  )
}

export default CoverPageProvider
